import React, { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import io from "socket.io-client";
import { API_URL } from "../../config";
import { hasPermission } from "../../helpers/permission";
import "./chatsf.css";
import streetfolioLogo from "../../assets/img/streetfolioLogo.png";

const baseUrl = API_URL ? (API_URL.endsWith("/") ? API_URL : `${API_URL}/`) : "/";
const CHAT_API_URL = `${baseUrl}research/chat-sf`;
const CHAT_SOCKET_URL = process.env.REACT_APP_CENTRAL_SOCKET_URL || "https://socket.streetgains.in:8086";

const formatTime = (date = new Date()) => {
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });
};

const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
};

const getAuthHeaders = () => {
  const token = localStorage.getItem("authToken") || localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const mapServerMessage = (item) => {
  const type = item.message_type || (item.image_url ? "image" : item.file_url ? "file" : "text");
  let timestamp = formatTime();
  if (item.created_on) {
    try {
      const d = new Date(item.created_on);
      if (!isNaN(d.getTime())) {
        timestamp = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });
      }
    } catch {
      // fallback
    }
  }

  const messageContent = item.message || "";
  const resolvedImageUrl = item.image_url || (type === "image" ? item.file_url : null);
  const resolvedFileUrl = type === "file" ? item.file_url : (type !== "image" ? item.file_url : null);

  return {
    id: item.id,
    type: type,
    heading: item.heading || "",
    text: messageContent,
    imageUrl: resolvedImageUrl,
    fileUrl: resolvedFileUrl,
    fileName: item.file_name || "",
    fileSize: item.file_size || "",
    fileType: item.file_type || "",
    caption: messageContent,
    timestamp: timestamp,
    status: item.delivery_status || "Sent",
    users: (item.s_type !== undefined && item.s_type !== null && item.s_type !== "" && item.s_type !== "0")
      ? String(item.s_type)
      : (item.users !== undefined && item.users !== null && item.users !== "" && item.users !== "0" ? String(item.users) : null),
    s_type: (item.s_type !== undefined && item.s_type !== null && item.s_type !== "" && item.s_type !== "0")
      ? String(item.s_type)
      : (item.users !== undefined && item.users !== null && item.users !== "" && item.users !== "0" ? String(item.users) : null),
    userStatus: item.status !== undefined && item.status !== null ? Number(item.status) : null,
    services: item.services !== undefined && item.services !== null && item.services !== "" ? String(item.services) : null,
    serviceSlug: item.service_slug || null,
    serviceName: item.service_name || null,
    filterstring: item.filterstring || null,
    isVisible: item.isvisible !== undefined && item.isvisible !== null ? Number(item.isvisible) : 0,
  };
};

// Auto-link URLs in HTML while avoiding existing <a> tags
const autoLinkUrls = (html) => {
  if (!html) return "";

  const tokens = html.split(/(<[^>]+>)/g);
  let insideAnchor = false;

  const processed = tokens.map((token) => {
    if (!token) return "";

    if (token.startsWith("<") && token.endsWith(">")) {
      if (/^<a\b/i.test(token)) {
        insideAnchor = true;
      } else if (/^<\/a\s*>/i.test(token)) {
        insideAnchor = false;
      }
      return token;
    }

    if (insideAnchor) {
      return token;
    }

    return token.replace(/(https?:\/\/[^\s<]+|www\.[^\s<]+)/gi, (matched) => {
      let cleanUrl = matched;
      let trailing = "";

      while (cleanUrl.length > 0) {
        const lastChar = cleanUrl[cleanUrl.length - 1];
        if ([".", ",", ";", ":", "!", "'", '"'].includes(lastChar)) {
          trailing = lastChar + trailing;
          cleanUrl = cleanUrl.slice(0, -1);
        } else if (
          lastChar === ")" &&
          (cleanUrl.match(/\(/g) || []).length < (cleanUrl.match(/\)/g) || []).length
        ) {
          trailing = lastChar + trailing;
          cleanUrl = cleanUrl.slice(0, -1);
        } else {
          break;
        }
      }

      if (!cleanUrl) return matched;

      const href = cleanUrl.startsWith("www.") ? `https://${cleanUrl}` : cleanUrl;
      return `<a href="${href}" target="_blank" rel="noopener noreferrer">${cleanUrl}</a>${trailing}`;
    });
  });

  return processed.join("");
};

// Rich text parsing helper for *bold*, _italic_, ~strike~, `code`, and links
const parseInlineFormatting = (text) => {
  if (!text) return text;

  const INLINE_REGEX = /(https?:\/\/[^\s]+)|(`[^`\n]+`)|(\*\*[^\n*]+\*\*|\*[^\n*]+\*)|(_[^\n_]+_)|(~~[^\n~]+~~|~[^\n~]+~)/g;
  const parts = text.split(INLINE_REGEX);

  return parts
    .filter((part) => part !== undefined && part !== "")
    .map((part, idx) => {
      // 1. Link (http / https)
      if (part.startsWith("http://") || part.startsWith("https://")) {
        return (
          <a
            key={idx}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="csg-link"
            onClick={(e) => e.stopPropagation()}
          >
            {part}
          </a>
        );
      }

      // 2. Monospace / Inline code: `...`
      if (part.startsWith("`") && part.endsWith("`") && part.length >= 2) {
        return (
          <code key={idx} className="csg-inline-code">
            {part.slice(1, -1)}
          </code>
        );
      }

      // 3. Bold: **...** or *...*
      if (part.startsWith("**") && part.endsWith("**") && part.length >= 4) {
        return <strong key={idx}>{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith("*") && part.endsWith("*") && part.length >= 2) {
        return <strong key={idx}>{part.slice(1, -1)}</strong>;
      }

      // 4. Italic: _..._
      if (part.startsWith("_") && part.endsWith("_") && part.length >= 2) {
        return <em key={idx}>{part.slice(1, -1)}</em>;
      }

      // 5. Strike: ~~...~~ or ~...~
      if (part.startsWith("~~") && part.endsWith("~~") && part.length >= 4) {
        return <del key={idx}>{part.slice(2, -2)}</del>;
      }
      if (part.startsWith("~") && part.endsWith("~") && part.length >= 2) {
        return <del key={idx}>{part.slice(1, -1)}</del>;
      }

      return part;
    });
};

const stripHtml = (html) => {
  if (!html) return "";
  return html
    .replace(/<br[^>]*>/gi, " ")
    .replace(/<\/p>/gi, " ")
    .replace(/<\/?[^>]+(>|$)/g, "")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&")
    .trim();
};

const htmlToEditText = (html) => {
  if (!html) return "";

  // 1. Decode entities if HTML tags or characters were escaped (&lt;strong..., &amp;, etc.)
  let text = html
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, " ");

  if (!/<\/?[a-z][\s\S]*>/i.test(text)) {
    return text.replace(/&amp;/g, "&").trim();
  }

  // 2. Convert standard tags (including tags with attributes like data-start, class, style) into clean markdown/newlines
  return text
    .replace(/<br[^>]*>/gi, "\n")
    .replace(/<\/p>\s*<p[^>]*>/gi, "\n\n")
    .replace(/<\/?div[^>]*>/gi, "\n")
    .replace(/<\/?p[^>]*>/gi, "\n")
    .replace(/<strong[^>]*>(.*?)<\/strong>/gi, "*$1*")
    .replace(/<b[^>]*>(.*?)<\/b>/gi, "*$1*")
    .replace(/<em[^>]*>(.*?)<\/em>/gi, "_$1_")
    .replace(/<i[^>]*>(.*?)<\/i>/gi, "_$1_")
    .replace(/<del[^>]*>(.*?)<\/del>/gi, "~$1~")
    .replace(/<s>(.*?)<\/s>/gi, "~$1~")
    .replace(/<code[^>]*>(.*?)<\/code>/gi, "`$1`")
    .replace(/<a[^>]*href=["']([^"']+)["'][^>]*>(.*?)<\/a>/gi, "$1")
    .replace(/<li[^>]*>(.*?)<\/li>/gi, "• $1\n")
    .replace(/<[^>]+>/g, "") // Strip any remaining unhandled HTML tags
    .replace(/&amp;/g, "&")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const renderFormattedContent = (content) => {
  if (!content) return null;

  // Render HTML content directly if stored with HTML tags (<div><p>...</p></div>)
  if (/<\/?[a-z][\s\S]*>/i.test(content)) {
    let htmlToRender = content;
    // Decode escaped HTML tags if present (e.g. &lt;strong... from previously saved edits)
    if (/&lt;(strong|b|em|i|del|s|code|br|a|p|div)[^&]*&gt;/i.test(htmlToRender)) {
      htmlToRender = htmlToRender
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'");
    }

    htmlToRender = autoLinkUrls(htmlToRender);

    return (
      <div
        className="csg-rendered-html"
        dangerouslySetInnerHTML={{ __html: htmlToRender }}
      />
    );
  }

  const lines = content.split("\n");

  return lines.map((line, lineIdx) => {
    const isBullet = line.trim().startsWith("- ") || line.trim().startsWith("• ");
    const lineText = isBullet ? line.trim().substring(2) : line;

    return (
      <React.Fragment key={lineIdx}>
        {lineIdx > 0 && <br />}
        {isBullet ? (
          <span className="csg-formatted-bullet">
            <span className="csg-bullet-dot">•</span>
            <span>{parseInlineFormatting(lineText)}</span>
          </span>
        ) : (
          parseInlineFormatting(lineText)
        )}
      </React.Fragment>
    );
  });
};

// value format: "{sType}-{status}" except All Users which is "0"
// sType (clean code): 0=All, 1=Paid, 2=Free
// status: 1=Active, 2=Expired
const USER_TYPE_OPTIONS = [
  { value: "0",   label: "All Users",    sType: "0", status: null }, // no restriction
  { value: "1-1", label: "Paid Active",  sType: "1", status: 1 },
  { value: "1-2", label: "Paid Expired", sType: "1", status: 2 },
  { value: "2-1", label: "Free Active",  sType: "2", status: 1 },
  { value: "2-2", label: "Free Expired", sType: "2", status: 2 },
  { value: "0-1", label: "All Active",   sType: "0", status: 1 },
  { value: "0-2", label: "All Expired",  sType: "0", status: 2 },
];

const getUserLabel = (usersVal, statusVal) => {
  if (usersVal === undefined || usersVal === null || usersVal === "") return null;
  if (statusVal !== undefined && statusVal !== null && statusVal !== "") {
    const key = `${usersVal}-${statusVal}`;
    const foundCombo = USER_TYPE_OPTIONS.find((opt) => opt.value === key);
    if (foundCombo) return foundCombo.label;
  }
  const found = USER_TYPE_OPTIONS.find((opt) => String(opt.sType) === String(usersVal));
  return found ? found.label : (String(usersVal) === "0" ? null : `Service Type ${usersVal}`);
};

const getServiceLabel = (serviceVal, serviceName, servicesOptions = []) => {
  if (serviceName) return serviceName;
  if (!serviceVal) return null;
  const found = servicesOptions.find((opt) => String(opt.value) === String(serviceVal));
  return found ? found.label : `Service #${serviceVal}`;
};

export default function ChatSf() {
  const canAdd = hasPermission("chat_sf", "add");
  const canEdit = hasPermission("chat_sf", "edit");
  const canDelete = hasPermission("chat_sf", "delete");

  const PAGE_SIZE = 50;
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [socketConnected, setSocketConnected] = useState(false);

  // User and Services filter dropdown states
  const [userType, setUserType] = useState("0");
  const [serviceId, setServiceId] = useState("0"); // "0" = All Services (default)
  const [servicesOptions, setServicesOptions] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [heading, setHeading] = useState("");

  const handleClearForm = () => {
    if (editorRef.current) {
      editorRef.current.innerHTML = "";
    }
    setIsEditorEmpty(true);
    setInputText("");
    setHeading("");
    setAttachment(null);
    setUserType("0");
    setServiceId("0");
    focusEditor();
  };

  const [inputText, setInputText] = useState("");
  const [isEditorEmpty, setIsEditorEmpty] = useState(true);
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    strikethrough: false,
    code: false,
    bullet: false,
  });
  const [attachment, setAttachment] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [deleteModalMsg, setDeleteModalMsg] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editHeading, setEditHeading] = useState("");
  const [editText, setEditText] = useState("");

  const fileInputRef = useRef(null);
  const editorRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messageLogRef = useRef(null);
  const dragCounterRef = useRef(0);
  const prevSendingRef = useRef(false);

  // Helper to focus input field and place cursor at end
  const focusEditor = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    try {
      const sel = window.getSelection();
      if (sel) {
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        sel.removeAllRanges();
        sel.addRange(range);
      }
    } catch {
      // ignore
    }
  }, []);

  // Auto-focus input field on default page mount and when messages finish loading
  useEffect(() => {
    if (canAdd && !loading) {
      const timer = setTimeout(() => {
        focusEditor();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [canAdd, loading, focusEditor]);

  // Re-focus input field as soon as message sending completes
  useEffect(() => {
    if (prevSendingRef.current && !sending && canAdd) {
      const timer = setTimeout(() => {
        focusEditor();
      }, 50);
      return () => clearTimeout(timer);
    }
    prevSendingRef.current = sending;
  }, [sending, canAdd, focusEditor]);

  // Close modals on Escape key
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (e.key === "Escape") {
        if (deleteModalMsg) setDeleteModalMsg(null);
        if (lightboxImage) setLightboxImage(null);
      }
    };
    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown);
  }, [deleteModalMsg, lightboxImage]);

  const scrollToBottom = useCallback((instant = true) => {
    const performScroll = () => {
      const container = messageLogRef.current;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
      if (messagesEndRef.current) {
        try {
          messagesEndRef.current.scrollIntoView({
            behavior: instant ? "auto" : "smooth",
            block: "end",
          });
        } catch {
          // fallback
        }
      }
    };

    performScroll();
    requestAnimationFrame(performScroll);
    setTimeout(performScroll, 50);
    setTimeout(performScroll, 150);
    setTimeout(performScroll, 350);
  }, []);

  const fetchMessages = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${CHAT_API_URL}?limit=${PAGE_SIZE}`, {
        headers: getAuthHeaders(),
      });
      if (Array.isArray(res.data)) {
        setMessages(res.data.map(mapServerMessage));
        setHasMore(res.data.length >= PAGE_SIZE);
      }
    } catch (err) {
      console.error("Failed to load chat messages:", err);
    } finally {
      setLoading(false);
      setTimeout(() => {
        scrollToBottom(true);
      }, 50);
    }
  }, [scrollToBottom]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Fetch services from API (portfolio-services for Streetfolio)
  const fetchServices = useCallback(async () => {
    try {
      setServicesLoading(true);
      const res = await axios.get(`${baseUrl}service/portfolio-services`, {
        headers: getAuthHeaders(),
      });
      if (res.data && res.data.success && Array.isArray(res.data.data)) {
        const services = res.data.data.map((service) => ({
          value: service.id.toString(),
          label: service.service_name,
          slug: service.service_slug || null,
        }));
        setServicesOptions(services);
      }
    } catch (err) {
      console.error("Error loading services for SF Messages:", err);
    } finally {
      setServicesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  // Real-time Central WebSocket connection (Room: sf_messages)
  useEffect(() => {
    const socket = io(CHAT_SOCKET_URL, {
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: 15,
      reconnectionDelay: 2000,
    });

    socket.on("connect", () => {
      setSocketConnected(true);
      socket.emit("joinRoom", "sf_messages");
    });

    socket.on("disconnect", () => {
      setSocketConnected(false);
    });

    socket.on("messageEvent", (payload) => {
      if (!payload || payload.group_id !== "sf_messages") return;

      switch (payload.action) {
        case "new_message": {
          const newMsg = payload.data;
          if (!newMsg || !newMsg.id) return;
          setMessages((prev) => {
            if (prev.some((m) => String(m.id) === String(newMsg.id))) {
              return prev;
            }
            return [...prev, mapServerMessage(newMsg)];
          });
          break;
        }

        case "update_message": {
          const updatedMsg = payload.data;
          if (!updatedMsg || !updatedMsg.id) return;
          setMessages((prev) =>
            prev.map((m) =>
              String(m.id) === String(updatedMsg.id) ? mapServerMessage(updatedMsg) : m
            )
          );
          break;
        }

        case "delete_message": {
          const rawId = typeof payload.data === "object" ? payload.data?.id : payload.data;
          if (!rawId) return;
          setMessages((prev) => prev.filter((m) => String(m.id) !== String(rawId)));
          break;
        }

        default:
          break;
      }
    });

    return () => {
      socket.off("connect");
      socket.off("disconnect");
      socket.off("messageEvent");
      socket.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!loading && messages.length > 0) {
      scrollToBottom(true);
    }
  }, [loading, messages.length, scrollToBottom]);

  const checkActiveFormats = useCallback(() => {
    try {
      const bold = document.queryCommandState("bold");
      const italic = document.queryCommandState("italic");
      const strike = document.queryCommandState("strikeThrough");
      const bullet = document.queryCommandState("insertUnorderedList");

      let isCode = false;
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        let node = sel.anchorNode;
        while (node && node !== editorRef.current && node.parentNode) {
          if (node.nodeName === "CODE") {
            isCode = true;
            break;
          }
          node = node.parentNode;
        }
      }

      setActiveFormats({
        bold: Boolean(bold),
        italic: Boolean(italic),
        strikethrough: Boolean(strike),
        code: Boolean(isCode),
        bullet: Boolean(bullet),
      });
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    const handleSelectionChange = () => {
      const activeEl = document.activeElement;
      if (
        editorRef.current &&
        (activeEl === editorRef.current || editorRef.current.contains(activeEl))
      ) {
        checkActiveFormats();
      }
    };
    document.addEventListener("selectionchange", handleSelectionChange);
    return () => document.removeEventListener("selectionchange", handleSelectionChange);
  }, [checkActiveFormats]);

  const handleEditorInput = () => {
    const el = editorRef.current;
    if (!el) return;
    const text = (el.innerText || "").trim();
    const hasSpecialChildren = Boolean(el.querySelector("img, ul, ol, li"));
    const empty = text === "" && !hasSpecialChildren;
    setIsEditorEmpty(empty);
    setInputText(text);
    checkActiveFormats();
  };

  // Handle File Selection
  const processFile = (file) => {
    if (!file) return;

    const fileName = file.name || "";
    const ext = fileName.includes(".")
      ? "." + fileName.split(".").pop().toLowerCase()
      : "";
    const allowedImageExts = [".jpg", ".jpeg", ".png"];
    const allowedDocExts = [".pdf", ".xls", ".xlsx", ".doc", ".docx"];

    const isAllowedImage =
      allowedImageExts.includes(ext) ||
      file.type === "image/jpeg" ||
      file.type === "image/png" ||
      file.type === "image/jpg";
    const isAllowedDoc = allowedDocExts.includes(ext);

    if (!isAllowedImage && !isAllowedDoc) {
      alert(
        "Unsupported file type!\n\nAllowed formats:\n• Images: JPG, JPEG, PNG\n• Documents: PDF, Excel (.xls, .xlsx), Word (.doc, .docx)"
      );
      return;
    }

    if (isAllowedImage) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setAttachment({
          file,
          name: file.name,
          size: formatBytes(file.size),
          isImage: true,
          previewUrl: e.target.result,
        });
        setTimeout(() => {
          focusEditor();
        }, 50);
      };
      reader.readAsDataURL(file);
    } else {
      setAttachment({
        file,
        name: file.name,
        size: formatBytes(file.size),
        isImage: false,
        previewUrl: null,
      });
      setTimeout(() => {
        focusEditor();
      }, 50);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
    e.target.value = "";
  };

  const removeAttachment = () => {
    setAttachment(null);
  };

  // Helper to sanitize pasted HTML (strips noisy attributes like data-start, data-end, inline styles)
  const sanitizePastedHtml = (rawHtml) => {
    try {
      const doc = new DOMParser().parseFromString(rawHtml, "text/html");
      const cleanElement = (el) => {
        const attrs = Array.from(el.attributes || []);
        attrs.forEach((attr) => {
          if (el.tagName.toLowerCase() === "a" && attr.name.toLowerCase() === "href") {
            return;
          }
          el.removeAttribute(attr.name);
        });
        Array.from(el.children).forEach(cleanElement);
      };
      cleanElement(doc.body);
      return doc.body.innerHTML;
    } catch {
      return rawHtml;
    }
  };

  // Clipboard paste support (paste image/file directly into chat or clean rich text)
  const handlePaste = (e) => {
    const file = e.clipboardData?.files?.[0];
    if (file) {
      e.preventDefault();
      processFile(file);
      return;
    }

    const htmlData = e.clipboardData?.getData("text/html");
    if (htmlData) {
      e.preventDefault();
      const cleaned = sanitizePastedHtml(htmlData);
      document.execCommand("insertHTML", false, cleaned);
      handleEditorInput();
    }
  };

  // Drag & Drop
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounterRef.current = 0;
    if (!canAdd) return;
    const file = e.dataTransfer.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  // Send Message
  const handleSend = async () => {
    if (!canAdd) return;
    const el = editorRef.current;
    const rawText = el ? (el.innerText || "").trim() : "";
    let htmlContent = el ? (el.innerHTML || "").trim() : "";
    const cleanHeading = (heading || "").trim();

    if (!rawText && !attachment && !cleanHeading) return;
    if (sending) return;

    let messagePayload = "";
    if (rawText) {
      if (htmlContent === "<br>" || htmlContent === "<div><br></div>" || htmlContent === "<p><br></p>") {
        messagePayload = "";
      } else if (/^<div[\s\S]*<\/div>$/i.test(htmlContent)) {
        messagePayload = htmlContent;
      } else if (/<ul|<ol|<div|<h\d/i.test(htmlContent)) {
        messagePayload = `<div>${htmlContent}</div>`;
      } else if (/^<p[\s\S]*<\/p>$/i.test(htmlContent)) {
        messagePayload = `<div>${htmlContent}</div>`;
      } else {
        messagePayload = `<div><p>${htmlContent}</p></div>`;
      }

      if (messagePayload) {
        messagePayload = autoLinkUrls(messagePayload);
        messagePayload = messagePayload
          .replace(/\s+data-[a-z0-9_-]+="[^"]*"/gi, "")
          .replace(/\s+data-[a-z0-9_-]+='[^']*'/gi, "");
      }
    }

    try {
      setSending(true);
      const formData = new FormData();
      if (cleanHeading) {
        formData.append("heading", cleanHeading);
      }
      if (messagePayload) {
        formData.append("message", messagePayload);
      }
      if (attachment?.file) {
        formData.append("file", attachment.file);
        formData.append("message_type", attachment.isImage ? "image" : "file");
      } else {
        formData.append("message_type", "text");
      }
      formData.append("delivery_status", "Sent");

      // Find the selected user type option to extract sType code and status
      const selectedUserOpt = USER_TYPE_OPTIONS.find((opt) => opt.value === userType);
      const resolvedSType = selectedUserOpt?.sType ?? "0";
      const resolvedStatus = selectedUserOpt?.status ?? null; // null = All Users (no restriction)

      formData.append("s_type", resolvedSType);
      formData.append("users", resolvedSType);
      formData.append("usertype", resolvedSType);

      // Always send services ("0" = All Services)
      formData.append("services", serviceId || "0");
      formData.append("serviceid", serviceId || "0");

      // Append service_slug when a specific service (not "0") is selected
      if (serviceId && serviceId !== "0") {
        const selectedService = servicesOptions.find((s) => s.value === serviceId);
        if (selectedService?.slug) {
          formData.append("service_slug", selectedService.slug);
        }
      }

      // Filterstring & isvisible logic:
      // Truly public ONLY when BOTH service is "0" (All Services) AND userType is "0" (All Users)
      const targetServiceId = serviceId || "0";
      const isAllServices = targetServiceId === "0";
      const isAllUsers = userType === "0";

      if (isAllServices && isAllUsers) {
        // All Services AND All Users → truly public message, no blur
        formData.append("isvisible", "0");
      } else {
        // Any restriction (specific service OR specific user type) → isvisible = 1
        const filterSType = resolvedSType; // "0" (All), "1" (Paid), "2" (Free)
        const dbStatus = isAllUsers ? 1 : resolvedStatus; // All Users for specific service targets active (1)

        const filterstring = `${targetServiceId}-${filterSType}-${dbStatus}`;
        formData.append("status", String(dbStatus));
        formData.append("filterstring", filterstring);
        formData.append("isvisible", "1");
      }

      const res = await axios.post(CHAT_API_URL, formData, {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "multipart/form-data",
        },
      });

      if (res.data && res.data.id) {
        const newMsg = mapServerMessage(res.data);
        setMessages((prev) => {
          if (prev.some((m) => String(m.id) === String(newMsg.id))) {
            return prev;
          }
          return [...prev, newMsg];
        });

        if (el) {
          el.innerHTML = "";
        }
        setIsEditorEmpty(true);
        setInputText("");
        setHeading("");
        setAttachment(null);
        setUserType("0");
        setServiceId("0");
        setActiveFormats({
          bold: false,
          italic: false,
          strikethrough: false,
          code: false,
          bullet: false,
        });
      }
    } catch (err) {
      console.error("Failed to send message:", err);
      alert("Failed to send message. Please check connection and try again.");
    } finally {
      setSending(false);
      setTimeout(() => {
        focusEditor();
      }, 50);
    }
  };

  const applyFormat = (type) => {
    const el = editorRef.current;
    if (!el) return;
    el.focus();

    if (type === "bold") {
      document.execCommand("bold", false, null);
    } else if (type === "italic") {
      document.execCommand("italic", false, null);
    } else if (type === "strikethrough") {
      document.execCommand("strikeThrough", false, null);
    } else if (type === "bullet") {
      const sel = window.getSelection();
      const rawText = (el.innerText || "").trim();

      if (!rawText) {
        el.innerHTML = "<ul><li><br></li></ul>";
        setIsEditorEmpty(false);
        const li = el.querySelector("li");
        if (li && sel) {
          const range = document.createRange();
          range.setStart(li, 0);
          range.collapse(true);
          sel.removeAllRanges();
          sel.addRange(range);
        }
      } else {
        document.execCommand("insertUnorderedList", false, null);
      }
    } else if (type === "code") {
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        if (!sel.isCollapsed) {
          // Check if selection is already inside <code>
          let node = sel.anchorNode;
          let codeParent = null;
          while (node && node !== el && node.parentNode) {
            if (node.nodeName === "CODE") {
              codeParent = node;
              break;
            }
            node = node.parentNode;
          }

          if (codeParent) {
            const parent = codeParent.parentNode;
            while (codeParent.firstChild) {
              parent.insertBefore(codeParent.firstChild, codeParent);
            }
            parent.removeChild(codeParent);
          } else {
            const codeEl = document.createElement("code");
            codeEl.className = "csg-inline-code";
            try {
              range.surroundContents(codeEl);
            } catch {
              codeEl.appendChild(range.extractContents());
              range.insertNode(codeEl);
            }
          }
        } else {
          document.execCommand("insertHTML", false, `<code class="csg-inline-code">&#8203;</code>`);
        }
      }
    } else if (type === "link") {
      const sel = window.getSelection();
      let selectedText = "";
      let hasSelection = false;
      if (sel && sel.rangeCount > 0 && !sel.isCollapsed) {
        selectedText = sel.toString().trim();
        hasSelection = true;
      }

      let defaultUrl = "https://";
      if (selectedText && /^https?:\/\//i.test(selectedText)) {
        defaultUrl = selectedText;
      }

      const inputUrl = window.prompt("Enter URL (http:// or https://):", defaultUrl);
      if (inputUrl && inputUrl.trim()) {
        let cleanUrl = inputUrl.trim();
        if (!/^https?:\/\//i.test(cleanUrl)) {
          cleanUrl = `https://${cleanUrl}`;
        }
        const displayText = hasSelection && selectedText ? selectedText : cleanUrl;
        const linkHtml = `<a href="${cleanUrl}" target="_blank" rel="noopener noreferrer">${displayText}</a>`;
        document.execCommand("insertHTML", false, linkHtml);
      }
    }

    handleEditorInput();
    checkActiveFormats();
  };

  const handleEditorKeyDown = (e) => {
    // Formatting keyboard shortcuts
    if (e.ctrlKey || e.metaKey) {
      if (e.key === "b" || e.key === "B") {
        e.preventDefault();
        applyFormat("bold");
        return;
      } else if (e.key === "i" || e.key === "I") {
        e.preventDefault();
        applyFormat("italic");
        return;
      } else if (e.key === "u" || e.key === "U") {
        e.preventDefault();
        applyFormat("strikethrough");
        return;
      } else if (e.key === "e" || e.key === "E") {
        e.preventDefault();
        applyFormat("code");
        return;
      }
    }

    // Enter key behavior:
    if (e.key === "Enter") {
      // If cursor is inside a list item, let Enter create the next bullet item
      let isInsideList = false;
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        let node = sel.anchorNode;
        while (node && node !== editorRef.current && node.parentNode) {
          if (node.nodeName === "LI" || node.nodeName === "UL" || node.nodeName === "OL") {
            isInsideList = true;
            break;
          }
          node = node.parentNode;
        }
      }

      if (isInsideList) {
        // Let the browser naturally insert the next <li> item
        setTimeout(() => {
          handleEditorInput();
          checkActiveFormats();
        }, 10);
        return;
      }

      // Outside a list, Enter just creates a new line (sending is only via the Send button)
      return;
    }
  };

  // Delete message confirmation via custom modal
  const handleConfirmDelete = async () => {
    if (!canDelete) return;
    if (!deleteModalMsg) return;
    const id = deleteModalMsg.id;

    try {
      setActionLoadingId(id);
      await axios.delete(`${CHAT_API_URL}/${id}`, {
        headers: getAuthHeaders(),
      });
      setMessages((prev) => prev.filter((m) => m.id !== id));
      setDeleteModalMsg(null);
    } catch (err) {
      console.error("Failed to delete message:", err);
      alert("Failed to delete message. Please try again.");
    } finally {
      setActionLoadingId(null);
    }
  };

  // Edit message
  const handleStartEdit = (msg) => {
    if (!canEdit) return;
    setEditingId(msg.id);
    setEditHeading(msg.heading || "");
    setEditText(htmlToEditText(msg.text || msg.caption || ""));
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditHeading("");
    setEditText("");
  };

  const handleSaveEdit = async (id) => {
    if (!canEdit) return;
    const trimmed = editText.trim();
    if (!trimmed) return;

    try {
      setActionLoadingId(id);
      const payload = { message: trimmed };
      if (editHeading !== undefined) {
        payload.heading = editHeading.trim();
      }
      const res = await axios.put(
        `${CHAT_API_URL}/${id}`,
        payload,
        { headers: getAuthHeaders() }
      );

      if (res.data) {
        const updatedMsg = mapServerMessage(res.data);
        setMessages((prev) =>
          prev.map((m) => (m.id === id ? updatedMsg : m))
        );
      }
      setEditingId(null);
      setEditHeading("");
      setEditText("");
    } catch (err) {
      console.error("Failed to update message:", err);
      alert("Failed to update message. Please try again.");
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleEditKeyDown = (e, id) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSaveEdit(id);
    } else if (e.key === "Escape") {
      e.preventDefault();
      handleCancelEdit();
    }
  };

  const renderDeliveryStatus = (status) => {
    let statusClass = "status-sent";

    if (status === "Delivered") {
      statusClass = "status-delivered";
    } else if (status === "Read") {
      statusClass = "status-read";
    } else if (status === "Failed") {
      statusClass = "status-failed";
    }

    return (
      <span className={`csg-delivery-status ${statusClass}`} title={status || "Sent"} aria-label={status || "Sent"}>
        {status === "Failed" ? (
          <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        ) : status === "Delivered" || status === "Read" ? (
          <svg viewBox="0 0 18 14" width="13" height="10" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1.5 7.5L5.5 11.5L14 2" />
            <path d="M5.5 7.5L9.5 11.5L17.5 2" />
          </svg>
        ) : (
          <svg viewBox="0 0 16 14" width="11" height="10" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="2 7.5 6 11.5 14 2.5" />
          </svg>
        )}
      </span>
    );
  };

  const handleDownloadFile = async (e, fileUrl, fileName) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!fileUrl) return;

    try {
      const response = await fetch(fileUrl);
      if (!response.ok) throw new Error("Fetch failed");
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = fileName || "download";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
    } catch (err) {
      const a = document.createElement("a");
      a.href = fileUrl;
      a.download = fileName || "download";
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  };

  const handleLoadMore = async () => {
    if (loadingMore || messages.length === 0) return;

    const oldestId = messages[0]?.id;
    if (!oldestId) return;

    const container = messageLogRef.current;
    const prevScrollHeight = container ? container.scrollHeight : 0;
    const prevScrollTop = container ? container.scrollTop : 0;

    try {
      setLoadingMore(true);
      const res = await axios.get(
        `${CHAT_API_URL}?limit=${PAGE_SIZE}&before_id=${oldestId}`,
        { headers: getAuthHeaders() }
      );

      if (Array.isArray(res.data)) {
        if (res.data.length === 0) {
          setHasMore(false);
        } else {
          const olderMessages = res.data.map(mapServerMessage);
          setMessages((prev) => {
            const existingIds = new Set(prev.map((m) => String(m.id)));
            const filteredOlder = olderMessages.filter(
              (m) => !existingIds.has(String(m.id))
            );
            return [...filteredOlder, ...prev];
          });
          setHasMore(res.data.length >= PAGE_SIZE);

          requestAnimationFrame(() => {
            if (container) {
              const newScrollHeight = container.scrollHeight;
              container.scrollTop =
                newScrollHeight - prevScrollHeight + prevScrollTop;
            }
          });
        }
      }
    } catch (err) {
      console.error("Failed to load older messages:", err);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div
      className="csg-wrapper"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Full-width Card Panel */}
      <div className="csg-panel">
        {/* Top Header */}
        <header className="csg-header">
          <div className="csg-header-left">
            <img
              src={streetfolioLogo}
              alt="Streetfolio"
              className="csg-header-logo"
            />
          </div>
          <div className="csg-header-right">
            <span className="csg-chat-count-badge">
              {messages.length} {messages.length === 1 ? "message" : "messages"}
            </span>
          </div>
        </header>

        {/* Main 2-Column Split: Form (Left) & Chats (Right) */}
        <div className="csg-split-layout">
          {/* LEFT SIDE: Composer Form */}
          {canAdd ? (
            <aside className="csg-form-panel">
              <div className="csg-form-header">
                <div className="csg-form-title-row">
                  <div className="csg-form-icon-circle">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                  </div>
                  <div>
                    <h2 className="csg-form-title">Compose Message</h2>
                    <span className="csg-form-subtitle">Create broadcast update or report</span>
                  </div>
                </div>
              </div>

              {/* User & Services Dropdowns */}
              <div className="csg-form-filters-row">
                <div className="csg-form-field">
                  <label htmlFor="csg-select-usertype" className="csg-field-label">
                    Service Type
                  </label>
                  <div className="csg-select-wrap">
                    <select
                      id="csg-select-usertype"
                      className="csg-select-input"
                      value={userType}
                      onChange={(e) => setUserType(e.target.value)}
                      disabled={sending}
                    >
                      {USER_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <span className="csg-select-arrow" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" width="13" height="13">
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </span>
                  </div>
                </div>

                <div className="csg-form-field">
                  <label htmlFor="csg-select-service" className="csg-field-label">
                    Services
                  </label>
                  <div className="csg-select-wrap">
                    <select
                      id="csg-select-service"
                      className="csg-select-input"
                      value={serviceId}
                      onChange={(e) => setServiceId(e.target.value)}
                      disabled={sending || servicesLoading}
                    >
                      <option value="0">{servicesLoading ? "Loading services..." : "All Services"}</option>
                      {servicesOptions.map((srv) => (
                        <option key={srv.value} value={srv.value}>
                          {srv.label}
                        </option>
                      ))}
                    </select>
                    <span className="csg-select-arrow" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" width="13" height="13">
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </span>
                  </div>
                </div>
              </div>

              {/* Heading Field before Textarea */}
              <div className="csg-form-field csg-heading-field">
                <label htmlFor="csg-heading-input" className="csg-field-label">
                  Heading
                </label>
                <input
                  id="csg-heading-input"
                  type="text"
                  className="csg-text-input"
                  placeholder="Enter heading (optional)..."
                  value={heading}
                  onChange={(e) => setHeading(e.target.value)}
                  disabled={sending}
                  maxLength={255}
                />
              </div>

              {/* Rich Text Editor Box with directly displayed toolbar */}
              <div className="csg-editor-container">
                {/* Directly Displayed Formatting Toolbar (No on/off system) */}
                <div className="csg-editor-toolbar" role="toolbar" aria-label="Text formatting">
                  <button
                    type="button"
                    className={`csg-toolbar-btn ${activeFormats.bold ? "is-active" : ""}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => applyFormat("bold")}
                    title="Bold (Ctrl+B)"
                    aria-label="Bold text"
                    aria-pressed={activeFormats.bold}
                  >
                    <strong>B</strong>
                  </button>
                  <button
                    type="button"
                    className={`csg-toolbar-btn ${activeFormats.italic ? "is-active" : ""}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => applyFormat("italic")}
                    title="Italic (Ctrl+I)"
                    aria-label="Italic text"
                    aria-pressed={activeFormats.italic}
                  >
                    <em>I</em>
                  </button>
                  <button
                    type="button"
                    className={`csg-toolbar-btn ${activeFormats.strikethrough ? "is-active" : ""}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => applyFormat("strikethrough")}
                    title="Strikethrough (Ctrl+U)"
                    aria-label="Strikethrough text"
                    aria-pressed={activeFormats.strikethrough}
                  >
                    <s>S</s>
                  </button>
                  <button
                    type="button"
                    className={`csg-toolbar-btn ${activeFormats.code ? "is-active" : ""}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => applyFormat("code")}
                    title="Inline Code (Ctrl+E)"
                    aria-label="Code text"
                    aria-pressed={activeFormats.code}
                  >
                    <code>&lt;/&gt;</code>
                  </button>
                  <div className="csg-toolbar-divider" />
                  <button
                    type="button"
                    className={`csg-toolbar-btn ${activeFormats.bullet ? "is-active" : ""}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => applyFormat("bullet")}
                    title="Bullet List"
                    aria-label="Bullet list"
                    aria-pressed={activeFormats.bullet}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13">
                      <line x1="9" y1="6" x2="20" y2="6"></line>
                      <line x1="9" y1="12" x2="20" y2="12"></line>
                      <line x1="9" y1="18" x2="20" y2="18"></line>
                      <circle cx="4" cy="6" r="1.5" fill="currentColor"></circle>
                      <circle cx="4" cy="12" r="1.5" fill="currentColor"></circle>
                      <circle cx="4" cy="18" r="1.5" fill="currentColor"></circle>
                    </svg>
                  </button>
                  <button
                    type="button"
                    className="csg-toolbar-btn"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => applyFormat("link")}
                    title="Insert Link"
                    aria-label="Insert Link"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13">
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                    </svg>
                  </button>
                </div>

                {/* Big Rich ContentEditable Textarea */}
                <div
                  ref={editorRef}
                  className="csg-editor-editable big-size"
                  contentEditable={!sending}
                  autoFocus
                  role="textbox"
                  aria-multiline="true"
                  data-empty={isEditorEmpty}
                  data-placeholder={
                    attachment
                      ? `Add an optional message with this ${attachment.isImage ? "image" : "file"}...`
                      : "Type your broadcast message or report here..."
                  }
                  onInput={handleEditorInput}
                  onKeyDown={handleEditorKeyDown}
                  onKeyUp={checkActiveFormats}
                  onMouseUp={checkActiveFormats}
                  onPaste={handlePaste}
                />
              </div>

              {/* Hidden file input */}
              <input
                type="file"
                ref={fileInputRef}
                className="csg-hidden-file-input"
                accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,image/jpeg,image/png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={handleFileChange}
              />

              {/* File Attachment Section */}
              <div className="csg-form-attach-wrap">
                {!attachment ? (
                  <div
                    className="csg-attach-drop-zone"
                    onClick={() => fileInputRef.current?.click()}
                    role="button"
                    tabIndex={0}
                    title="Click or drag file here to attach"
                  >
                    <div className="csg-attach-drop-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
                      </svg>
                    </div>
                    <div className="csg-attach-drop-text">
                      <span className="csg-attach-drop-title">Attach File or Image</span>
                      <span className="csg-attach-drop-desc">PDF, Word, Excel, JPG, PNG (or paste directly)</span>
                    </div>
                    <button type="button" className="csg-attach-browse-badge">
                      Browse
                    </button>
                  </div>
                ) : (
                  <div className="csg-attach-preview-box">
                    <div className="csg-attach-preview-content">
                      {attachment.isImage ? (
                        <img src={attachment.previewUrl} alt="Thumbnail preview" className="csg-attach-preview-thumb" />
                      ) : (
                        <div className="csg-attach-preview-file-icon">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                            <polyline points="14 2 14 8 20 8"></polyline>
                          </svg>
                        </div>
                      )}
                      <div className="csg-attach-preview-meta">
                        <span className="csg-attach-preview-name" title={attachment.name}>{attachment.name}</span>
                        <span className="csg-attach-preview-sub">
                          <span className="csg-attach-type-tag">{attachment.isImage ? "Image" : "Document"}</span>
                          <span>{attachment.size}</span>
                        </span>
                      </div>
                    </div>
                    <div className="csg-attach-preview-actions">
                      <button
                        type="button"
                        className="csg-attach-change-btn"
                        onClick={() => fileInputRef.current?.click()}
                        title="Change file"
                      >
                        Change
                      </button>
                      <button
                        type="button"
                        className="csg-attach-preview-remove"
                        onClick={removeAttachment}
                        title="Remove attachment"
                        aria-label="Remove attachment"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="14" height="14">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Form Actions Footer */}
              <div className="csg-form-footer">
                <button
                  type="button"
                  className="csg-submit-btn"
                  onClick={handleSend}
                  disabled={(isEditorEmpty && !attachment) || sending}
                >
                  {sending ? (
                    <>
                      <svg className="csg-spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" strokeDasharray="32" strokeDashoffset="12" strokeLinecap="round" />
                      </svg>
                      <span>Sending Broadcast...</span>
                    </>
                  ) : (
                    <>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                        <line x1="22" y1="2" x2="11" y2="13"></line>
                        <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                      </svg>
                      <span>Send Broadcast</span>
                    </>
                  )}
                </button>

                {(!isEditorEmpty || attachment || heading) && (
                  <div className="csg-form-helpers-row">
                    <button
                      type="button"
                      className="csg-clear-btn"
                      onClick={handleClearForm}
                      disabled={sending}
                      title="Clear draft"
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>
            </aside>
          ) : (
            <aside className="csg-form-panel csg-view-only">
              <div className="csg-view-only-box">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="28" height="28">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                <p>You have view-only access to SF Messages.</p>
              </div>
            </aside>
          )}

          {/* RIGHT SIDE: Chat Messages Feed */}
          <section className="csg-chat-panel">
            {/* Message Log Area */}
            <main ref={messageLogRef} className="csg-message-log" role="log" aria-live="polite">
          {loading ? (
            <div className="csg-loading-state">
              <div className="csg-loading-spinner"></div>
              <div className="csg-loading-text">Loading messages...</div>
            </div>
          ) : messages.length === 0 ? (
            <div className="csg-empty-state">
              <div className="csg-empty-icon-wrap">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                </svg>
              </div>
              <div className="csg-empty-title">No message sent yet</div>
              <div className="csg-empty-subtitle">
                Compose an update, share charts, or attach research reports to broadcast to SF Messages.
              </div>
            </div>
          ) : (
            <>
              {/* Load Earlier Messages (Pagination Button) */}
              {hasMore && (
                <div className="csg-load-more-wrap">
                  <button
                    type="button"
                    className="csg-load-more-btn"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    title="Click to view previous 50 messages"
                  >
                    {loadingMore ? (
                      <>
                        <div
                          className="csg-loading-spinner"
                          style={{ width: 13, height: 13, borderWidth: 2 }}
                        ></div>
                        <span>Loading earlier messages...</span>
                      </>
                    ) : (
                      <>
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="csg-load-more-icon"
                        >
                          <polyline points="18 15 12 9 6 15"></polyline>
                        </svg>
                        <span>View More Messages (Load earlier)</span>
                      </>
                    )}
                  </button>
                </div>
              )}
              {messages.map((msg) => {
              const isEditing = editingId === msg.id;

              return (
                <div key={msg.id} className="csg-message-row">
                  {/* Left Hover Actions (Edit & Delete) */}
                  {!isEditing && (canEdit || canDelete) && (
                    <div className="csg-row-actions">
                      {canEdit && (
                        <button
                          type="button"
                          className="csg-action-btn edit-btn"
                          title="Edit message"
                          onClick={() => handleStartEdit(msg)}
                          aria-label="Edit message"
                          disabled={actionLoadingId === msg.id}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                          </svg>
                        </button>
                      )}
                      {canDelete && (
                        <button
                          type="button"
                          className="csg-action-btn delete-btn"
                          title="Delete message"
                          onClick={() => setDeleteModalMsg(msg)}
                          aria-label="Delete message"
                          disabled={actionLoadingId === msg.id}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          </svg>
                        </button>
                      )}
                    </div>
                  )}

                  {/* Outgoing Blue Bubble */}
                  <div className="csg-bubble">
                    {msg.heading && !isEditing && (
                      <div className="csg-bubble-heading">
                        {msg.heading}
                      </div>
                    )}

                    {/* TYPE: Text */}
                    {msg.type === "text" && (
                      <>
                        {isEditing ? (
                          <div className="csg-inline-edit-box">
                            <input
                              type="text"
                              className="csg-edit-heading-input"
                              placeholder="Edit heading (optional)..."
                              value={editHeading}
                              onChange={(e) => setEditHeading(e.target.value)}
                              maxLength={255}
                            />
                            <textarea
                              className="csg-edit-textarea"
                              value={editText}
                              onChange={(e) => setEditText(e.target.value)}
                              onKeyDown={(e) => handleEditKeyDown(e, msg.id)}
                              autoFocus
                              rows={3}
                            />
                            <div className="csg-edit-controls">
                              <span className="csg-edit-hint">Enter to save • Esc to cancel</span>
                              <button
                                type="button"
                                className="csg-edit-btn csg-edit-cancel-btn"
                                onClick={handleCancelEdit}
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                className="csg-edit-btn csg-edit-save-btn"
                                onClick={() => handleSaveEdit(msg.id)}
                                disabled={actionLoadingId === msg.id}
                              >
                                {actionLoadingId === msg.id ? "Saving..." : "Save"}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="csg-bubble-text">{renderFormattedContent(msg.text)}</div>
                        )}
                      </>
                    )}

                    {/* TYPE: File */}
                    {msg.type === "file" && (
                      <div className="csg-file-chip-wrap">
                        {msg.fileUrl ? (
                          <div className="csg-file-chip">
                            <div className="csg-file-icon-box">
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                <polyline points="14 2 14 8 20 8"></polyline>
                                <line x1="16" y1="13" x2="8" y2="13"></line>
                                <line x1="16" y1="17" x2="8" y2="17"></line>
                                <polyline points="10 9 9 9 8 9"></polyline>
                              </svg>
                            </div>
                            <div className="csg-file-meta">
                              <span className="csg-file-name" title={msg.fileName}>
                                {msg.fileName}
                              </span>
                              <span className="csg-file-size">{msg.fileSize}</span>
                            </div>
                            <button
                              type="button"
                              className="csg-file-action-download"
                              onClick={(e) => handleDownloadFile(e, msg.fileUrl, msg.fileName || "document")}
                              title={`Download ${msg.fileName || "file"}`}
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" width="14" height="14">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                <polyline points="7 10 12 15 17 10"></polyline>
                                <line x1="12" y1="15" x2="12" y2="3"></line>
                              </svg>
                              <span>Download</span>
                            </button>
                          </div>
                        ) : (
                          <div className="csg-file-chip">
                            <div className="csg-file-icon-box">
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                <polyline points="14 2 14 8 20 8"></polyline>
                                <line x1="16" y1="13" x2="8" y2="13"></line>
                                <line x1="16" y1="17" x2="8" y2="17"></line>
                                <polyline points="10 9 9 9 8 9"></polyline>
                              </svg>
                            </div>
                            <div className="csg-file-meta">
                              <span className="csg-file-name" title={msg.fileName}>
                                {msg.fileName}
                              </span>
                              <span className="csg-file-size">{msg.fileSize}</span>
                            </div>
                          </div>
                        )}

                        {isEditing ? (
                          <div className="csg-inline-edit-box">
                            <input
                              type="text"
                              className="csg-edit-heading-input"
                              placeholder="Edit heading (optional)..."
                              value={editHeading}
                              onChange={(e) => setEditHeading(e.target.value)}
                              maxLength={255}
                            />
                            <textarea
                              className="csg-edit-textarea"
                              value={editText}
                              onChange={(e) => setEditText(e.target.value)}
                              onKeyDown={(e) => handleEditKeyDown(e, msg.id)}
                              autoFocus
                              rows={2}
                              placeholder="Edit message..."
                            />
                            <div className="csg-edit-controls">
                              <span className="csg-edit-hint">Enter to save • Esc to cancel</span>
                              <button
                                type="button"
                                className="csg-edit-btn csg-edit-cancel-btn"
                                onClick={handleCancelEdit}
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                className="csg-edit-btn csg-edit-save-btn"
                                onClick={() => handleSaveEdit(msg.id)}
                                disabled={actionLoadingId === msg.id}
                              >
                                {actionLoadingId === msg.id ? "Saving..." : "Save"}
                              </button>
                            </div>
                          </div>
                        ) : (
                          msg.text && <div className="csg-file-caption">{renderFormattedContent(msg.text)}</div>
                        )}
                      </div>
                    )}

                    {/* TYPE: Image */}
                    {msg.type === "image" && (
                      <>
                        <div
                          className="csg-image-wrap"
                          onClick={() => setLightboxImage({ url: msg.imageUrl, caption: msg.caption, fileName: msg.fileName })}
                          title="Click to view full image"
                        >
                          <img
                            src={msg.imageUrl}
                            alt={msg.fileName || "Uploaded attachment"}
                            loading="lazy"
                            onLoad={() => scrollToBottom(true)}
                          />
                          <div className="csg-image-top-bar">
                            <button
                              type="button"
                              className="csg-img-btn csg-img-download-btn"
                              onClick={(e) => handleDownloadFile(e, msg.imageUrl, msg.fileName || "chat_image.jpg")}
                              title="Download image"
                              aria-label="Download image"
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" width="13" height="13">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                <polyline points="7 10 12 15 17 10"></polyline>
                                <line x1="12" y1="15" x2="12" y2="3"></line>
                              </svg>
                              <span>Download</span>
                            </button>
                            <div className="csg-img-btn csg-img-zoom-btn" title="Click to zoom">
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13">
                                <circle cx="11" cy="11" r="8"></circle>
                                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                              </svg>
                            </div>
                          </div>
                        </div>

                        {isEditing ? (
                          <div className="csg-inline-edit-box">
                            <input
                              type="text"
                              className="csg-edit-heading-input"
                              placeholder="Edit heading (optional)..."
                              value={editHeading}
                              onChange={(e) => setEditHeading(e.target.value)}
                              maxLength={255}
                            />
                            <textarea
                              className="csg-edit-textarea"
                              value={editText}
                              onChange={(e) => setEditText(e.target.value)}
                              onKeyDown={(e) => handleEditKeyDown(e, msg.id)}
                              autoFocus
                              rows={2}
                              placeholder="Edit image caption..."
                            />
                            <div className="csg-edit-controls">
                              <span className="csg-edit-hint">Enter to save • Esc to cancel</span>
                              <button
                                type="button"
                                className="csg-edit-btn csg-edit-cancel-btn"
                                onClick={handleCancelEdit}
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                className="csg-edit-btn csg-edit-save-btn"
                                onClick={() => handleSaveEdit(msg.id)}
                                disabled={actionLoadingId === msg.id}
                              >
                                {actionLoadingId === msg.id ? "Saving..." : "Save"}
                              </button>
                            </div>
                          </div>
                        ) : (
                          msg.caption && <div className="csg-image-caption">{renderFormattedContent(msg.caption)}</div>
                        )}
                      </>
                    )}

                    {/* Bubble Footer Row: Target Highlights (Bottom & Small) + Meta (Timestamp & Status) */}
                    <div className="csg-bubble-footer-row">
                      {(Boolean(getUserLabel(msg.users, msg.status)) || msg.services || msg.serviceName) ? (
                        <div className="csg-bubble-targets-bottom">
                          {Boolean(getUserLabel(msg.users, msg.status)) && (
                            <span className="csg-target-tag-sm csg-target-user-sm" title={`Service Type: ${getUserLabel(msg.users, msg.status)}`}>
                              <span>{getUserLabel(msg.users, msg.status)}</span>
                            </span>
                          )}
                          {(msg.services || msg.serviceName) && (
                            <span className="csg-target-tag-sm csg-target-service-sm" title={`Target Service: ${getServiceLabel(msg.services, msg.serviceName, servicesOptions)}`}>
                              <span>{getServiceLabel(msg.services, msg.serviceName, servicesOptions)}</span>
                            </span>
                          )}
                        </div>
                      ) : <div />}

                      <div className="csg-bubble-meta-row">
                        {msg.edited && <span className="csg-edited-tag">edited</span>}
                        <span className="csg-timestamp">{msg.timestamp}</span>
                        {renderDeliveryStatus(msg.status)}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )}
        <div ref={messagesEndRef} />
      </main>
    </section>
  </div>

        {/* Drag and Drop Blue Dashed Overlay */}
        {isDragging && (
          <div className="csg-drop-overlay">
            <div className="csg-drop-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="17 8 12 3 7 8"></polyline>
                <line x1="12" y1="3" x2="12" y2="15"></line>
              </svg>
            </div>
            <div className="csg-drop-title">Drop file to attach</div>
            <div className="csg-drop-subtitle">Images and documents will be attached directly to this message</div>
          </div>
        )}
      </div>

      {/* Fullscreen Lightbox / Zoom Overlay */}
      {lightboxImage && (
        <div
          className="csg-lightbox-modal"
          onClick={() => setLightboxImage(null)}
          role="dialog"
          aria-modal="true"
        >
          <div className="csg-lightbox-header" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="csg-lightbox-dl-btn"
              onClick={(e) => handleDownloadFile(e, lightboxImage.url, lightboxImage.fileName || "image.jpg")}
              title="Download image"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" width="15" height="15">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
              <span>Download</span>
            </button>
            <button
              type="button"
              className="csg-lightbox-close"
              onClick={() => setLightboxImage(null)}
              title="Close viewer (Esc)"
              aria-label="Close image viewer"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          <div className="csg-lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img src={lightboxImage.url} alt="Expanded view" className="csg-lightbox-img" />
            {lightboxImage.caption && (
              <div className="csg-lightbox-caption">{lightboxImage.caption}</div>
            )}
          </div>
        </div>
      )}

      {/* Custom Delete Confirmation Modal */}
      {deleteModalMsg && (
        <div
          className="csg-modal-overlay"
          onClick={() => !actionLoadingId && setDeleteModalMsg(null)}
          role="dialog"
          aria-modal="true"
        >
          <div className="csg-modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="csg-modal-icon-wrap">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                <line x1="10" y1="11" x2="10" y2="17"></line>
                <line x1="14" y1="11" x2="14" y2="17"></line>
              </svg>
            </div>

            <h3 className="csg-modal-title">Delete Message?</h3>
            <p className="csg-modal-desc">
              Are you sure you want to delete this message? This action cannot be undone.
            </p>

            {/* Message preview snippet */}
            <div className="csg-modal-preview">
              {deleteModalMsg.type === "text" && (
                <span className="csg-modal-preview-text">
                  "{stripHtml(deleteModalMsg.text).length > 90
                    ? `${stripHtml(deleteModalMsg.text).slice(0, 90)}...`
                    : stripHtml(deleteModalMsg.text)}"
                </span>
              )}
              {deleteModalMsg.type === "file" && (
                <span className="csg-modal-preview-file">
                  📎 {deleteModalMsg.fileName || "Attached file"}
                </span>
              )}
              {deleteModalMsg.type === "image" && (
                <div className="csg-modal-preview-img-row">
                  {deleteModalMsg.imageUrl && (
                    <img src={deleteModalMsg.imageUrl} alt="preview" className="csg-modal-preview-thumb" />
                  )}
                  <span>
                    {stripHtml(deleteModalMsg.caption) || deleteModalMsg.fileName || "Attached image"}
                  </span>
                </div>
              )}
            </div>

            <div className="csg-modal-actions">
              <button
                type="button"
                className="csg-modal-btn csg-modal-cancel-btn"
                onClick={() => setDeleteModalMsg(null)}
                disabled={actionLoadingId === deleteModalMsg.id}
              >
                Cancel
              </button>
              <button
                type="button"
                className="csg-modal-btn csg-modal-delete-btn"
                onClick={handleConfirmDelete}
                disabled={actionLoadingId === deleteModalMsg.id}
              >
                {actionLoadingId === deleteModalMsg.id ? (
                  <>
                    <svg className="csg-spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ width: 14, height: 14 }}>
                      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" strokeDasharray="32" strokeDashoffset="12" strokeLinecap="round" />
                    </svg>
                    <span>Deleting...</span>
                  </>
                ) : (
                  "Delete"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
